import { RBox } from '../../render/parts'
import { LoungeChair } from '../parts/seating'
import type { AssetContext, AssetDefinition } from '../types'

const WIDTH = 0.8
const DEPTH = 0.78

/**
 * Top of the reclined backrest: the LoungeChair back cushion is centred at
 * y = 0.60 with a half-height of 0.22 and a 0.1 rad recline, so it tops out at
 * ~0.826. Rounded up rather than the nominal 0.76 so bounds do not clip it.
 */
const HEIGHT = 0.83

/** Seat cushion top of the shared LoungeChair — where the throw cushion rests. */
const SEAT_TOP = 0.42

/**
 * Loose throw cushion leaning into the backrest. Deliberately overlapping the
 * seat and the back by ~1 cm so no two faces end up coplanar.
 */
function ThrowCushion({ accent, highlight }: { accent?: string; highlight: boolean }) {
  return (
    <RBox
      tone={highlight ? 'accent' : 'panel'}
      color={highlight ? accent : undefined}
      size={[0.3, 0.3, 0.1]}
      radius={0.035}
      position={[0.07, SEAT_TOP + 0.15, -0.12]}
      rotation={[-0.35, 0.14, 0]}
    />
  )
}

/**
 * Breakout lounge armchair. Thin wooden splayed legs, one-piece seat and back
 * cushion, low arms — the shared `LoungeChair` sub-assembly does all of it; the
 * only thing this asset adds is the throw cushion that carries the accent.
 */
function ArmchairLounge({ accent, variant = 'claro' }: AssetContext) {
  return (
    <group>
      <LoungeChair />
      <ThrowCushion accent={accent} highlight={variant === 'acento'} />
    </group>
  )
}

export const definition: AssetDefinition = {
  id: 'armchair-lounge',
  name: 'Sillón lounge',
  category: 'seating',
  footprint: [WIDTH, DEPTH],
  height: HEIGHT,
  seats: 1,
  occupantSide: 'front',
  seatInset: 0.39,
  accentable: true,
  variants: [
    { id: 'claro', name: 'Claro' },
    { id: 'acento', name: 'Acento' },
  ],
  keywords: [
    'sillón',
    'sofá individual',
    'butaca',
    'lounge',
    'armchair',
    'easy chair',
    'sala de espera',
  ],
  Component: ArmchairLounge,
}
